import React, { useContext, useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  UncontrolledDropdown
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import ShopeeV2Api from "../../../services/shopeeV2";
import "./ShopeeInventory.scss";

// Renders either a real provider value or an i18n message id fallback
const IdOrText = ({ value }) =>
  value && typeof value === "string" && value.startsWith("shopee.") ? (
    <IntlMessages id={value} />
  ) : (
    value || "—"
  );

// Currency formatter
const money = (val) => Number(val || 0).toLocaleString("vi-VN") + " đ";

// Channel Icon Badge SVG / Marker
const ChannelIcon = ({ type, size = 18 }) => {
  if (type === "shopee") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#EE4D2D",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        S
      </span>
    );
  }
  if (type === "tiktok") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#000",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        ♪
      </span>
    );
  }
  if (type === "lazada") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#0F146D",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        L
      </span>
    );
  }
  if (type === "tiki") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#1A94FF",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        T
      </span>
    );
  }
  if (type === "website") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#4B5563",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        🌐
      </span>
    );
  }
  if (type === "pos") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          borderRadius: 4,
          backgroundColor: "#2563EB",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold",
          marginRight: 6
        }}
      >
        🏪
      </span>
    );
  }
  return null;
};

export default injectIntl(function ShopeeShippingPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const realOrders = workspace?.data?.orders || [];

  // State management
  const [activeTab, setActiveTab] = useState("all");
  const [activeStep, setActiveStep] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [selectedCarrier, setSelectedCarrier] = useState("all");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState({
    c1: true,
    c2: true,
    c3: true,
    c4: false,
    c5: false
  });
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [scanInput, setScanInput] = useState("");

  // Orders mapping real workspace data
  const ordersList = useMemo(() => {
    return realOrders.map((o, idx) => ({
      id: o.id || idx + 1,
      order_sn: o.order_sn || `SP-${o.id}`,
      channel: "shopee",
      channel_name: "Shopee",
      customer_name:
        o.recipient_name ||
        o.buyer_username ||
        intl.formatMessage(
          { id: "shopee.workspace.shipping.customer-fallback" },
          { number: idx + 1 }
        ),
      customer_phone: o.recipient_phone || o.recipient_address?.phone || "",
      customer_address: o.recipient_address?.full_address || o.recipient_address || "—",
      is_vip: false,
      product_name: o.items?.[0]?.item_name || intl.formatMessage({ id: "shopee.workspace.shipping.product-fallback" }),
      product_sku: o.items?.[0]?.item_sku || "",
      product_qty: o.items?.[0]?.model_quantity_purchased || 1,
      product_price: Number(o.total_amount || 0),
      items: (o.items || []).map((item, pIdx) => ({
        id: item.id || pIdx + 1,
        name: item.item_name || intl.formatMessage({ id: "shopee.workspace.shipping.product-fallback" }),
        sku: item.item_sku || item.model_sku || "",
        qty: Number(item.model_quantity_purchased || item.quantity || 1),
        price: Number(item.model_discounted_price || item.item_price || 0),
        checked: true
      })),
      amount: Number(o.total_amount || 0),
      warehouse: "Kho HCM",
      carrier: o.shipping_carrier || "Shopee Logistics",
      carrier_badge: "SPX",
      tracking_code: o.tracking_number || "—",
      shipping_fee: Number(o.actual_shipping_fee || 0),
      cod_amount: Number(o.total_amount || 0),
      total_collect: Number(o.total_amount || 0),
      service_type: "shopee.workspace.shipping.service-standard",
      status_label:
        o.order_status === "READY_TO_SHIP"
          ? "shopee.workspace.shipping.status.ready-pickup"
          : o.order_status || "shopee.workspace.shipping.status.processing",
      status_color: "warning",
      progress_pct: o.order_status === "COMPLETED" ? 100 : 50,
      progress_color: o.order_status === "COMPLETED" ? "success" : "warning",
      checklist: [
        { id: "c1", label: "shopee.workspace.shipping.checklist.inspect", done: true },
        { id: "c2", label: "shopee.workspace.shipping.checklist.protect", done: true }
      ]
    }));
  }, [realOrders, intl]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return ordersList.filter((item) => {
      if (activeTab !== "all" && item.channel !== activeTab) return false;
      if (selectedChannel !== "all" && item.channel !== selectedChannel) return false;
      if (selectedCarrier !== "all" && item.carrier !== selectedCarrier) return false;
      if (selectedWarehouse !== "all" && item.warehouse !== selectedWarehouse) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchSn = item.order_sn.toLowerCase().includes(q);
        const matchCust = item.customer_name.toLowerCase().includes(q);
        const matchPhone = item.customer_phone.includes(q);
        const matchTracking = item.tracking_code.toLowerCase().includes(q);
        if (!matchSn && !matchCust && !matchPhone && !matchTracking) return false;
      }
      return true;
    });
  }, [ordersList, activeTab, selectedChannel, selectedCarrier, selectedWarehouse, searchQuery]);

  // Currently selected active order for detail panel
  const activeOrder = useMemo(() => {
    return ordersList.find((o) => o.id === selectedRowId) || ordersList[0];
  }, [ordersList, selectedRowId]);

  // Toggle checklist items in detail panel
  const toggleChecklist = (id) => {
    setSelectedChecklist((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ReactTable Columns
  const columns = useMemo(
    () => [
      {
        Header: () => <input type="checkbox" style={{ cursor: "pointer" }} />,
        id: "selection",
        width: 38,
        sortable: false,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row._original.id === selectedRowId}
            onChange={() => setSelectedRowId(row._original.id)}
            style={{ cursor: "pointer" }}
          />
        )
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.orders.col.sn" />
            <small><IntlMessages id="shopee.workspace.shipping.col.order-sub" /></small>
          </div>
        ),
        accessor: "order_sn",
        minWidth: 130,
        Cell: ({ row }) => (
          <div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ChannelIcon type={row._original.channel} size={16} />
              <strong style={{ fontSize: "12px", color: "#1e293b" }}>{row._original.order_sn}</strong>
            </div>
            <span style={{ fontSize: "10px", color: "#64748b" }}>{row._original.channel_name}</span>
          </div>
        )
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.common.customer" />
            <small><IntlMessages id="shopee.workspace.shipping.col.phone-sub" /></small>
          </div>
        ),
        accessor: "customer_name",
        minWidth: 130,
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: "600", fontSize: "12px", color: "#1e293b" }}>{row._original.customer_name}</div>
            <div style={{ fontSize: "10px", color: "#64748b" }}>{row._original.customer_phone}</div>
          </div>
        )
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.catalog.col-product" />
            <small>(SKU)</small>
          </div>
        ),
        accessor: "product_name",
        minWidth: 160,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                flexShrink: 0
              }}
            >
              📦
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "12px",
                  color: "#1e293b",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {row._original.product_name && row._original.product_name.startsWith("shopee.") ? (
                  <IdOrText value={row._original.product_name} />
                ) : (
                  row._original.product_name
                )}{" "}
                <span style={{ color: "#2563eb", fontWeight: "700" }}>x{row._original.product_qty}</span>
              </div>
              <div style={{ fontSize: "10px", color: "#64748b" }}>({row._original.product_sku})</div>
            </div>
          </div>
        )
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.shipping.col.amount" />
            <small>(VND)</small>
          </div>
        ),
        accessor: "amount",
        minWidth: 90,
        Cell: ({ value }) => (
          <span style={{ fontWeight: "600", fontSize: "12px", color: "#0f172a" }}>
            {Number(value).toLocaleString("vi-VN")}
          </span>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.returns.col.warehouse" />,
        accessor: "warehouse",
        minWidth: 80,
        Cell: ({ value }) => <span style={{ fontSize: "11px", color: "#475569" }}>{value}</span>
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.orders.col.channel" />
            <small><IntlMessages id="shopee.workspace.orders.col.tracking" /></small>
          </div>
        ),
        accessor: "carrier",
        minWidth: 140,
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: "600", fontSize: "11px", color: "#1e293b" }}>{row._original.carrier}</div>
            <div style={{ fontSize: "10px", color: "#64748b", fontFamily: "monospace" }}>{row._original.tracking_code}</div>
          </div>
        )
      },
      {
        Header: () => (
          <div>
            <IntlMessages id="shopee.workspace.returns.col.status" />
            <small><IntlMessages id="shopee.workspace.shipping.col.progress-sub" /></small>
          </div>
        ),
        accessor: "status_label",
        minWidth: 140,
        Cell: ({ row }) => (
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color:
                    row._original.status_color === "success"
                      ? "#16a34a"
                      : row._original.status_color === "primary"
                      ? "#2563eb"
                      : row._original.status_color === "purple"
                      ? "#9333ea"
                      : "#d97706"
                }}
              >
                <IdOrText value={row._original.status_label} />
              </span>
              <span style={{ fontSize: "10px", fontWeight: "600", color: "#64748b" }}>{row._original.progress_pct}%</span>
            </div>
            <Progress
              value={row._original.progress_pct}
              style={{ height: "4px" }}
              color={
                row._original.status_color === "purple"
                  ? "secondary"
                  : row._original.status_color
              }
            />
          </div>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.actions" />,
        id: "actions",
        width: 100,
        sortable: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button
              color="link"
              size="sm"
              className="p-1 text-secondary"
              title={intl.formatMessage({ id: "shopee.workspace.shipping.print-slip-title" })}
              onClick={(e) => {
                e.stopPropagation();
                alert(
                  intl.formatMessage(
                    { id: "shopee.workspace.shipping.alert.print-slip" },
                    { sn: row._original.order_sn }
                  )
                );
              }}
            >
              <i className="simple-icon-printer" />
            </Button>
            <Button
              color="link"
              size="sm"
              className="p-1 text-secondary"
              title={intl.formatMessage({ id: "shopee.workspace.returns.view-detail-title" })}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowId(row._original.id);
              }}
            >
              <i className="simple-icon-eye" />
            </Button>
            <UncontrolledDropdown>
              <DropdownToggle color="link" size="sm" className="p-1 text-secondary">
                <i className="simple-icon-options-vertical" />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem
                  onClick={() =>
                    alert(
                      intl.formatMessage(
                        { id: "shopee.workspace.shipping.alert.update-status" },
                        { sn: row._original.order_sn }
                      )
                    )
                  }
                >
                  <IntlMessages id="shopee.workspace.shipping.action.update-status" />
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    alert(
                      intl.formatMessage(
                        { id: "shopee.workspace.shipping.alert.cancel-order" },
                        { sn: row._original.order_sn }
                      )
                    )
                  }
                >
                  <IntlMessages id="shopee.workspace.shipping.action.cancel-order" />
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        )
      }
    ],
    [selectedRowId, intl]
  );

  return (
    <div className="shopee-inventory">
      {/* 1. Header with Page Title & Action Buttons */}
      <div className="shopee-inventory__header">
        <div className="title-area">
          <h2><IntlMessages id="shopee.workspace.tab.shipping" /></h2>
          <p><IntlMessages id="shopee.workspace.shipping.subtitle" /></p>
        </div>
        <div className="actions-area">
          <Button color="outline-primary" onClick={() => setIsScanModalOpen(true)}>
            <i className="simple-icon-plus mr-1" />{" "}
            <IntlMessages id="shopee.workspace.shipping.create-shipping-order" />
          </Button>
          <Button color="outline-secondary" onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.alert.print-labels-bulk" }))}>
            <i className="simple-icon-printer mr-1" />{" "}
            <IntlMessages id="shopee.workspace.shipping.action.print-bulk" />
          </Button>
          <Button color="outline-secondary" onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.alert.export-excel" }))}>
            <i className="simple-icon-doc mr-1" />{" "}
            <IntlMessages id="shopee.workspace.returns.export-excel" />
          </Button>
          <Button color="primary" onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.alert.syncing-orders" }))}>
            <i className="simple-icon-refresh mr-1" />{" "}
            <IntlMessages id="shopee.workspace.orders.sync-button" />
          </Button>
        </div>
      </div>

      {/* 2. Top 6 KPI Metric Cards */}
      <div className="shopee-inventory__kpis">
        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-orange">
            <i className="simple-icon-shield" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.orders.metric.pending" /></span>
            <span className="value">32</span>
            <span className="subtext text-warning font-weight-bold">
              ⚠️{" "}
              <IntlMessages
                id="shopee.workspace.shipping.kpi.overdue"
                values={{ count: 5 }}
              />
            </span>
          </div>
        </div>

        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-blue">
            <i className="simple-icon-grid" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.shipping.kpi.packing" /></span>
            <span className="value">18</span>
            <span className="subtext"><IntlMessages id="shopee.workspace.shipping.kpi.packing-sub" /></span>
          </div>
        </div>

        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-orange">
            <i className="simple-icon-basket" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.shipping.status.ready-pickup" /></span>
            <span className="value">25</span>
            <span className="subtext text-danger font-weight-bold">
              ! <IntlMessages id="shopee.workspace.shipping.kpi.pickup-today" />
            </span>
          </div>
        </div>

        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-purple">
            <i className="simple-icon-check" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.shipping.kpi.handover" /></span>
            <span className="value">165</span>
            <span className="subtext"><IntlMessages id="shopee.workspace.overview.period.today" /></span>
          </div>
        </div>

        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-cyan">
            <i className="simple-icon-plane" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.shipping.kpi.shipping" /></span>
            <span className="value">41</span>
            <span className="subtext"><IntlMessages id="shopee.workspace.shipping.kpi.shipping-sub" /></span>
          </div>
        </div>

        <div className="shopee-inventory__kpi">
          <div className="shopee-inventory__kpi-icon is-green">
            <i className="simple-icon-check" />
          </div>
          <div className="shopee-inventory__kpi-info">
            <span className="label"><IntlMessages id="shopee.workspace.orders.status.completed" /></span>
            <span className="value">856</span>
            <span className="subtext"><IntlMessages id="shopee.workspace.shipping.step.delivered" /></span>
          </div>
        </div>
      </div>

      {/* 3. Process Flow Pipeline Bar */}
      <div className="shopee-inventory__process-flow">
        <div
          className={`flow-step ${activeStep === "confirm" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "confirm" ? "all" : "confirm")}
        >
          <i className="simple-icon-clock step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.returns.status.waiting-confirm" /></span>
          <span className="step-count">32</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "picking" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "picking" ? "all" : "picking")}
        >
          <i className="simple-icon-social-dropbox step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.step.picking" /></span>
          <span className="step-count">18</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "packing" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "packing" ? "all" : "packing")}
        >
          <i className="simple-icon-layers step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.kpi.packing" /></span>
          <span className="step-count">18</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "print" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "print" ? "all" : "print")}
        >
          <i className="simple-icon-printer step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.action.print-label" /></span>
          <span className="step-count">25</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "handover" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "handover" ? "all" : "handover")}
        >
          <i className="simple-icon-action-redo step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.kpi.handover" /></span>
          <span className="step-count">165</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "shipping" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "shipping" ? "all" : "shipping")}
        >
          <i className="simple-icon-paper-plane step-icon" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.kpi.shipping" /></span>
          <span className="step-count">41</span>
        </div>
        <span className="flow-arrow">➔</span>

        <div
          className={`flow-step ${activeStep === "delivered" ? "active" : ""}`}
          onClick={() => setActiveStep(activeStep === "delivered" ? "all" : "delivered")}
        >
          <i className="simple-icon-check step-icon text-success" />
          <span className="step-name"><IntlMessages id="shopee.workspace.shipping.step.delivered" /></span>
          <span className="step-count text-success">856</span>
        </div>
      </div>

      {/* 4. Filter Toolbar */}
      <div className="shopee-inventory__toolbar">
        <div className="search-box">
          <i className="simple-icon-magnifier" />
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: "shopee.workspace.shipping.search-placeholder" })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="select-box">
          <Input
            type="select"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-channels" })}</option>
            <option value="shopee">Shopee</option>
            <option value="tiktok">TikTok Shop</option>
            <option value="lazada">Lazada</option>
            <option value="tiki">Tiki</option>
            <option value="website">Website</option>
            <option value="pos">POS</option>
          </Input>
        </div>

        <div className="select-box">
          <Input
            type="select"
            value={selectedCarrier}
            onChange={(e) => setSelectedCarrier(e.target.value)}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.shipping.filter.all-carriers" })}</option>
            <option value="GHN Express">GHN Express</option>
            <option value="GHTK">GHTK</option>
            <option value="Lazada Logistics">Lazada Logistics</option>
            <option value="J&T Express">J&T Express</option>
            <option value="Viettel Post">Viettel Post</option>
          </Input>
        </div>

        <div className="select-box">
          <Input
            type="select"
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.returns.all-warehouses" })}</option>
            <option value="Kho HCM">Kho HCM</option>
            <option value="Kho HN">Kho HN</option>
          </Input>
        </div>

        <div className="select-box">
          <Input
            type="select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-statuses" })}</option>
            <option value="packing">{intl.formatMessage({ id: "shopee.workspace.shipping.kpi.packing" })}</option>
            <option value="ready">{intl.formatMessage({ id: "shopee.workspace.shipping.status.ready-pickup" })}</option>
            <option value="handover">{intl.formatMessage({ id: "shopee.workspace.shipping.kpi.handover" })}</option>
            <option value="shipping">{intl.formatMessage({ id: "shopee.workspace.shipping.kpi.shipping" })}</option>
          </Input>
        </div>

        <div className="select-box" style={{ flex: "0 1 110px" }}>
          <Input type="select" defaultValue="today">
            <option value="today">{intl.formatMessage({ id: "shopee.workspace.overview.period.today" })}</option>
            <option value="yesterday">{intl.formatMessage({ id: "shopee.workspace.common.yesterday" })}</option>
            <option value="7days">{intl.formatMessage({ id: "shopee.workspace.common.last-7-days" })}</option>
            <option value="this_month">{intl.formatMessage({ id: "shopee.workspace.common.this-month" })}</option>
          </Input>
        </div>

        <Button color="outline-secondary" className="btn-filter">
          <i className="simple-icon-equalizer" />{" "}
          <IntlMessages id="shopee.workspace.common.filter" />{" "}
          <Badge color="primary" pill>2</Badge>
        </Button>
      </div>

      {/* 5. Main Split View: Left Table + Right Order Detail Panel */}
      <div className="shopee-inventory__body">
        {/* Left Column: Multi-channel Tabs, Table, Performance Footer */}
        <div className="shopee-inventory__main-col">
          <div className="shopee-inventory__card">
            {/* Multi-channel Tabs Header */}
            <div className="shopee-inventory__channel-tabs">
              <button
                className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                <IntlMessages id="shopee.workspace.catalog.channel-all" /> <span className="badge-count">1.137</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "shopee" ? "active" : ""}`}
                onClick={() => setActiveTab("shopee")}
              >
                <ChannelIcon type="shopee" /> Shopee <span className="badge-count">425</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "tiktok" ? "active" : ""}`}
                onClick={() => setActiveTab("tiktok")}
              >
                <ChannelIcon type="tiktok" /> TikTok Shop <span className="badge-count">268</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "lazada" ? "active" : ""}`}
                onClick={() => setActiveTab("lazada")}
              >
                <ChannelIcon type="lazada" /> Lazada <span className="badge-count">198</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "tiki" ? "active" : ""}`}
                onClick={() => setActiveTab("tiki")}
              >
                <ChannelIcon type="tiki" /> Tiki <span className="badge-count">106</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "website" ? "active" : ""}`}
                onClick={() => setActiveTab("website")}
              >
                <ChannelIcon type="website" /> Website <span className="badge-count">88</span>
              </button>
              <button
                className={`tab-btn ${activeTab === "pos" ? "active" : ""}`}
                onClick={() => setActiveTab("pos")}
              >
                <ChannelIcon type="pos" /> POS <span className="badge-count">52</span>
              </button>
            </div>

            {/* ReactTable */}
            <div className="shopee-inventory__table-wrapper">
              <ReactTable
                data={filteredOrders}
                columns={columns}
                defaultPageSize={10}
                showPagination={false}
                className="-striped -highlight"
                getTrProps={(state, rowInfo) => {
                  if (!rowInfo) return {};
                  const isSelected = rowInfo.original.id === selectedRowId;
                  return {
                    onClick: () => setSelectedRowId(rowInfo.original.id),
                    style: {
                      background: isSelected ? "#ebf3fe" : "transparent",
                      cursor: "pointer"
                    }
                  };
                }}
              />
            </div>

            {/* Custom Pagination Footer */}
            <div className="shopee-inventory__pagination">
              <span className="info-text">
                <IntlMessages
                  id="shopee.workspace.shipping.pagination-summary"
                  values={{ count: filteredOrders.length, total: "1.137" }}
                />
              </span>
              <div className="controls">
                <button className="btn-page" disabled>&lt;</button>
                <button className="btn-page active">1</button>
                <button className="btn-page">2</button>
                <button className="btn-page">3</button>
                <button className="btn-page">4</button>
                <button className="btn-page">5</button>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>...</span>
                <button className="btn-page">57</button>
                <button className="btn-page">&gt;</button>
                <select className="page-size-select" defaultValue="20">
                  {["10", "20", "50"].map((size) => (
                    <option key={size} value={size}>
                      <IntlMessages
                        id="shopee.workspace.common.per-page"
                        values={{ size }}
                      />
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Performance Summary Footer Bar */}
          <div className="shipping-perf-footer">
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.performance-today" /></span>
              <span className="value">
                <IntlMessages id="shopee.workspace.shipping.perf.updated-at" />{" "}
                <i className="simple-icon-refresh text-muted" style={{ fontSize: "11px" }} />
              </span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.processed" /></span>
              <span className="value">
                <IntlMessages
                  id="shopee.workspace.shipping.perf.orders-value"
                  values={{ count: 245 }}
                />
              </span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.on-time" /></span>
              <span className="value is-green">94%</span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.overdue" /></span>
              <span className="value is-red">
                <IntlMessages
                  id="shopee.workspace.shipping.perf.orders-value"
                  values={{ count: 5 }}
                />
              </span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.success-rate" /></span>
              <span className="value is-green">96.2%</span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.returns" /></span>
              <span className="value is-blue">
                <IntlMessages
                  id="shopee.workspace.shipping.perf.orders-value"
                  values={{ count: 3 }}
                />
              </span>
            </div>
            <div className="perf-item">
              <span className="label"><IntlMessages id="shopee.workspace.shipping.perf.top-carriers" /></span>
              <span className="value" style={{ fontSize: "11px" }}>
                GHN <strong>48%</strong> &nbsp; GHTK <strong>28%</strong> &nbsp; J&amp;T <strong>12%</strong>
              </span>
            </div>
            <div>
              <Button color="link" size="sm" className="p-0 text-primary font-weight-bold" style={{ fontSize: "11px" }}>
                <IntlMessages id="shopee.workspace.returns.view-detail-title" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column: Order Detail Side Panel (SP24072201 active) */}
        <div className="shopee-inventory__side-col">
          {activeOrder ? (
            <div className="shipping-detail-panel">
              {/* Header */}
              <div className="panel-header">
                <div className="top-row">
                  <div className="title-row">
                    <h4>{activeOrder.order_sn}</h4>
                    <Badge color="warning" className="badge-status status-chenhlech">
                      <IdOrText value={activeOrder.status_label} />
                    </Badge>
                  </div>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-muted"
                    onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.alert.close-detail" }))}
                  >
                    ✕
                  </Button>
                </div>
                <div className="subtitle">
                  <ChannelIcon type={activeOrder.channel} size={14} />
                  <span>{activeOrder.channel_name}</span>
                  <span className="text-muted">|</span>
                  <span>{activeOrder.carrier}</span>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <div className="section-title"><IntlMessages id="shopee.workspace.shipping.section-customer" /></div>
                <div className="customer-box">
                  <div className="name-row">
                    <div className="left-part">
                      <span>{activeOrder.customer_name}</span>
                      {activeOrder.is_vip && <span className="badge-vip">VIP</span>}
                    </div>
                    <div className="btn-icon-group">
                      <span
                        className="btn-circle"
                        title={intl.formatMessage({ id: "shopee.workspace.shipping.title.call" })}
                        onClick={() =>
                          alert(
                            intl.formatMessage(
                              { id: "shopee.workspace.shipping.alert.call" },
                              { phone: activeOrder.customer_phone }
                            )
                          )
                        }
                      >
                        📞
                      </span>
                      <span
                        className="btn-circle"
                        title={intl.formatMessage({ id: "shopee.workspace.shipping.title.address" })}
                        onClick={() => alert(activeOrder.customer_address)}
                      >
                        📍
                      </span>
                    </div>
                  </div>
                  <div className="phone-row">
                    <span>📞 {activeOrder.customer_phone}</span>
                  </div>
                  <div className="address-row">
                    <span>📍 {activeOrder.customer_address}</span>
                  </div>
                </div>
              </div>

              {/* Product List */}
              <div>
                <div className="section-title">
                  <span>
                    <IntlMessages
                      id="shopee.workspace.shipping.section-products"
                      values={{ count: activeOrder.items?.length || 1 }}
                    />
                  </span>
                </div>
                {activeOrder.items?.map((item) => (
                  <div key={item.id} className="product-item">
                    <div className="img-wrap">
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "16px"
                        }}
                      >
                        👕
                      </div>
                    </div>
                    <div className="p-info">
                      <div className="p-name">
                        {item.name && item.name.startsWith("shopee.") ? (
                          <IdOrText value={item.name} />
                        ) : (
                          item.name
                        )}
                      </div>
                      <div className="p-sub">
                        {item.sku} &nbsp;|&nbsp;{" "}
                        <IntlMessages id="shopee.workspace.returns.qty-label" />:{" "}
                        <strong>{item.qty}</strong>
                      </div>
                    </div>
                    <div className="p-price">{money(item.price)}</div>
                  </div>
                ))}
              </div>

              {/* Shipping Details */}
              <div>
                <div className="section-title"><IntlMessages id="shopee.workspace.shipping.section-shipping-info" /></div>
                <div className="shipping-info-box">
                  <div className="info-row">
                    <span><IntlMessages id="shopee.workspace.shipping.col.carrier" /></span>
                    <span className="val">
                      {activeOrder.carrier}{" "}
                      <Badge color="danger" pill style={{ fontSize: "9px" }}>
                        {activeOrder.carrier_badge}
                      </Badge>
                    </span>
                  </div>
                  <div className="info-row">
                    <span><IntlMessages id="shopee.workspace.orders.col.tracking" /></span>
                    <span className="val" style={{ fontFamily: "monospace" }}>
                      {activeOrder.tracking_code}{" "}
                      <i
                        className="simple-icon-docs text-primary ml-1"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          alert(
                            intl.formatMessage(
                              { id: "shopee.workspace.shipping.alert.copied" },
                              { code: activeOrder.tracking_code }
                            )
                          )
                        }
                        title={intl.formatMessage({ id: "shopee.workspace.shipping.title.copy-code" })}
                      />
                    </span>
                  </div>
                  <div className="info-row">
                    <span><IntlMessages id="shopee.workspace.shipping.col.service" /></span>
                    <span className="val"><IdOrText value={activeOrder.service_type} /></span>
                  </div>
                  <div className="info-row">
                    <span><IntlMessages id="shopee.workspace.shipping.col.shipping-fee" /></span>
                    <span className="val">{money(activeOrder.shipping_fee)}</span>
                  </div>
                  <div className="info-row">
                    <span><IntlMessages id="shopee.workspace.shipping.col.cod" /></span>
                    <span className="val">{money(activeOrder.cod_amount)}</span>
                  </div>
                  <div className="info-row font-weight-bold" style={{ color: "#0f172a", fontSize: "12px" }}>
                    <span><IntlMessages id="shopee.workspace.shipping.col.total-collect" /></span>
                    <span className="val text-primary" style={{ fontSize: "13px" }}>
                      {money(activeOrder.total_collect)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Packing Checklist */}
              <div>
                <div className="section-title">
                  <span><IntlMessages id="shopee.workspace.shipping.checklist-title" /></span>
                  <span className="text-success" style={{ fontSize: "11px" }}>
                    3/5
                  </span>
                </div>
                <div className="checklist-box">
                  {activeOrder.checklist?.map((chk) => (
                    <label key={chk.id} className="check-item">
                      <input
                        type="checkbox"
                        checked={selectedChecklist[chk.id] ?? chk.done}
                        onChange={() => toggleChecklist(chk.id)}
                      />
                      <span
                        style={{
                          textDecoration: (selectedChecklist[chk.id] ?? chk.done) ? "line-through" : "none",
                          color: (selectedChecklist[chk.id] ?? chk.done) ? "#94a3b8" : "#334155"
                        }}
                      >
                        {chk.label && chk.label.startsWith("shopee.") ? (
                          <IdOrText value={chk.label} />
                        ) : (
                          chk.label
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Bottom Action Buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
                <Row className="no-gutters" style={{ gap: "8px" }}>
                  <Col>
                    <Button
                      outline
                      color="primary"
                      block
                      size="sm"
                      onClick={() =>
                        alert(
                          intl.formatMessage(
                            { id: "shopee.workspace.shipping.alert.print-label" },
                            { sn: activeOrder.order_sn }
                          )
                        )
                      }
                    >
                      <i className="simple-icon-printer mr-1" />{" "}
                      <IntlMessages id="shopee.workspace.shipping.action.print-label" />
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      outline
                      color="secondary"
                      block
                      size="sm"
                      onClick={() =>
                        alert(
                          intl.formatMessage(
                            { id: "shopee.workspace.shipping.alert.print-slip-order" },
                            { sn: activeOrder.order_sn }
                          )
                        )
                      }
                    >
                      <i className="simple-icon-doc mr-1" />{" "}
                      <IntlMessages id="shopee.workspace.shipping.action.print-slip" />
                    </Button>
                  </Col>
                </Row>

                <UncontrolledDropdown>
                  <DropdownToggle color="primary" block className="d-flex align-items-center justify-content-between">
                    <span><IntlMessages id="shopee.workspace.shipping.action.packed-done" /></span>
                    <i className="simple-icon-arrow-down" />
                  </DropdownToggle>
                  <DropdownMenu right style={{ width: "100%" }}>
                    <DropdownItem
                      onClick={() => {
                        if (workspace?.runAction) {
                          workspace.runAction(
                            `handover-${activeOrder.id}`,
                            (key) => ShopeeV2Api.remediateIssues(workspace.selectedShopId, "handover_order", [activeOrder.id], key),
                            intl.formatMessage({ id: "shopee.workspace.shipping.notice.packed-handover" })
                          );
                        } else {
                          alert(
                            intl.formatMessage(
                              { id: "shopee.workspace.shipping.alert.packed-done" },
                              { sn: activeOrder.order_sn }
                            )
                          );
                        }
                      }}
                    >
                      <IntlMessages id="shopee.workspace.shipping.action.complete-handover" />
                    </DropdownItem>
                    <DropdownItem onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.action.report-shortage" }))}>
                      <IntlMessages id="shopee.workspace.shipping.action.report-shortage" />
                    </DropdownItem>
                    <DropdownItem onClick={() => alert(intl.formatMessage({ id: "shopee.workspace.shipping.action.delay-packing" }))}>
                      <IntlMessages id="shopee.workspace.shipping.action.delay-packing" />
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Modal Scanner / Quick Action Drawer */}
      <Modal isOpen={isScanModalOpen} toggle={() => setIsScanModalOpen(false)} size="md">
        <ModalHeader toggle={() => setIsScanModalOpen(false)}>
          <IntlMessages id="shopee.workspace.shipping.scan.title" />
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            <IntlMessages id="shopee.workspace.shipping.scan.desc" />
          </p>
          <FormGroup>
            <Label><IntlMessages id="shopee.workspace.shipping.scan.label" /></Label>
            <Input
              type="text"
              placeholder={intl.formatMessage({ id: "shopee.workspace.shipping.scan.placeholder" })}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              autoFocus
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsScanModalOpen(false)}>
            <IntlMessages id="shopee.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={() => {
              if (!scanInput.trim()) return;
              const found = ordersList.find(
                (o) =>
                  o.order_sn.toLowerCase() === scanInput.trim().toLowerCase() ||
                  o.tracking_code.toLowerCase() === scanInput.trim().toLowerCase()
              );
              if (found) {
                setSelectedRowId(found.id);
                setIsScanModalOpen(false);
                setScanInput("");
              } else {
                alert(
                  intl.formatMessage(
                    { id: "shopee.workspace.shipping.alert.not-found" },
                    { code: scanInput }
                  )
                );
              }
            }}
          >
            <IntlMessages id="shopee.workspace.shipping.scan.now" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
});
